import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_BASE from '../config';
import { Banner, Loading, Empty, FlowBadge, formatDate, errorMessage } from '../components/ui';
import './Actions.css';

const TAB_KEYS = ['first', 'ready', 'renew', 'failed'];

/**
 * The work queue: what a manager has to do, grouped by the action needed
 * rather than by internal status.
 */
function Actions() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // The open tab lives in the URL so that opening an application and coming
  // back returns to the same queue, not the default one.
  const [params, setParams] = useSearchParams();
  const tab = TAB_KEYS.includes(params.get('tab')) ? params.get('tab') : 'first';
  const setTab = (key) => setParams(key === 'first' ? {} : { tab: key }, { replace: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/dashboard/actions`);
      setData(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the work queue.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="page-container"><div className="page-card"><Loading /></div></div>;
  }
  if (!data) {
    return <div className="page-container"><div className="page-card"><Banner kind="error" message={error} /></div></div>;
  }

  const TABS = [
    { key: 'first', label: 'New applications', n: data.firstTime.length, tone: 'amber' },
    { key: 'ready', label: 'Ready to send', n: data.readyToSend.length, tone: 'indigo' },
    { key: 'renew', label: 'Renewal due', n: data.renewalDue.length, tone: 'red' },
    { key: 'failed', label: 'Send failed', n: data.failed.length, tone: 'red' },
  ];

  // Carry where we came from, so the detail page's back button returns here
  // — to this tab — instead of dropping into the full list.
  const open = (id) => navigate(`/applications/${id}`, {
    state: { from: { to: `/actions${tab === 'first' ? '' : `?tab=${tab}`}`, label: 'Action Required' } },
  });

  /** What is stopping this application from being sent. */
  const nextStep = (r) => {
    if (r.Status === 'Send Failed') return 'Retry the send';
    if (!r.InternalAccountCode) return 'Capture the account code';
    if (r.IsCompliant === null || r.IsCompliant === undefined) return 'Record the decision';
    if (!r.InspectorCount) return 'Allocate an inspector';
    return 'Send to client';
  };

  return (
    <div className="page-container">
      <div className="page-card">
        <div className="page-header">
          <div>
            <h2>Action Required</h2>
            <p className="page-subtitle">
              Everything waiting on a manager. New applicants needing an approval or rejection
              email, and facilities whose certificate is due for reissue.
            </p>
          </div>
          <div className="page-header-actions">
            <button className="btn btn-secondary" onClick={load}>Refresh</button>
          </div>
        </div>

        <Banner kind="error" message={error} onDismiss={() => setError('')} />

        <div className="act-ribbon">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`act-card act-${t.tone}${tab === t.key ? ' is-on' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <span className="act-card-n">{t.n}</span>
              <span className="act-card-l">{t.label}</span>
            </button>
          ))}
        </div>

        {/* ---------- New applications ---------- */}
        {tab === 'first' && (
          data.firstTime.length === 0 ? (
            <Empty title="No new applications waiting." hint="First-time applicants needing an approval or rejection appear here." />
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Applied for</th>
                    <th className="nowrap">Waiting</th>
                    <th className="nowrap">Decision</th>
                    <th className="nowrap">Next step</th>
                  </tr>
                </thead>
                <tbody>
                  {data.firstTime.map((r) => (
                    <tr key={r.Id} className="row-clickable" onClick={() => open(r.Id)}>
                      <td>
                        <span className="act-name">{r.CompanyName || 'Unnamed'}</span>
                        <span className="act-sub">{r.ApplicantEmail || 'no email'}</span>
                      </td>
                      <td className="act-types">{r.CertificateTypesRaw || '—'}</td>
                      <td className={`nowrap ${r.AgeDays >= 14 ? 'act-old' : ''}`}>
                        {r.AgeDays} day{r.AgeDays === 1 ? '' : 's'}
                      </td>
                      <td className="nowrap">
                        {r.IsCompliant === null || r.IsCompliant === undefined
                          ? <span className="badge badge-grey">Not decided</span>
                          : <span className={`badge ${r.IsCompliant ? 'badge-green' : 'badge-red'}`}>
                              {r.IsCompliant ? 'Compliant' : 'Non-compliant'}
                            </span>}
                      </td>
                      <td className="nowrap act-next">{nextStep(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* ---------- Ready to send ---------- */}
        {tab === 'ready' && (
          data.readyToSend.length === 0 ? (
            <Empty title="Nothing waiting to be sent." hint="Applications that are decided, captured and allocated appear here." />
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Applicant</th><th className="nowrap">Route</th><th className="nowrap">Account</th><th className="nowrap">Documents</th><th className="nowrap">Waiting</th></tr>
                </thead>
                <tbody>
                  {data.readyToSend.map((r) => (
                    <tr key={r.Id} className="row-clickable" onClick={() => open(r.Id)}>
                      <td>
                        <span className="act-name">{r.CompanyName}</span>
                        <span className="act-sub">{r.ApplicantEmail}</span>
                      </td>
                      <td className="nowrap"><FlowBadge flowType={r.FlowType} /></td>
                      <td className="nowrap">{r.InternalAccountCode || '—'}</td>
                      <td className="numeric nowrap">{r.DocumentCount}</td>
                      <td className="nowrap">{r.AgeDays} days</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* ---------- Renewal due ---------- */}
        {tab === 'renew' && (
          data.renewalDue.length === 0 ? (
            <Empty
              title={`No certificates expiring within ${data.horizonDays} days.`}
              hint="Facilities needing a reissue appear here as their certificate approaches expiry."
            />
          ) : (
            <>
              <p className="act-hint">
                These hold a certificate at or near expiry. Open the record and use
                <strong> Issue renewal</strong> — it re-assesses compliance and starts a fresh
                validity period on the same application.
              </p>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>Facility</th><th className="nowrap">Account</th><th className="nowrap">Current certificate</th><th className="nowrap">Expires</th><th className="nowrap">Status</th></tr>
                  </thead>
                  <tbody>
                    {data.renewalDue.map((r) => (
                      <tr key={r.Id} className="row-clickable" onClick={() => open(r.Id)}>
                        <td>
                          <span className="act-name">{r.CompanyName}</span>
                          <span className="act-sub">{r.ApplicantEmail}</span>
                        </td>
                        <td className="nowrap">{r.InternalAccountCode}</td>
                        <td className="nowrap act-sub">{r.CertificateType} · {r.IssueDateText}</td>
                        <td className="nowrap">{formatDate(r.ValidUntil)}</td>
                        <td className="nowrap">
                          {r.DaysRemaining < 0
                            ? <span className="badge badge-red">Expired {Math.abs(r.DaysRemaining)}d ago</span>
                            : <span className={`badge ${r.DaysRemaining <= 7 ? 'badge-red' : 'badge-amber'}`}>
                                {r.DaysRemaining}d left
                              </span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        )}

        {/* ---------- Send failed ---------- */}
        {tab === 'failed' && (
          data.failed.length === 0 ? (
            <Empty title="No failed sends." />
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Applicant</th><th className="nowrap">Route</th><th>Error</th><th className="nowrap">When</th></tr>
                </thead>
                <tbody>
                  {data.failed.map((r) => (
                    <tr key={r.Id} className="row-clickable" onClick={() => open(r.Id)}>
                      <td>
                        <span className="act-name">{r.CompanyName}</span>
                        <span className="act-sub">{r.ApplicantEmail}</span>
                      </td>
                      <td className="nowrap"><FlowBadge flowType={r.FlowType} /></td>
                      <td className="act-error">{r.LastError || '—'}</td>
                      <td className="nowrap">{formatDate(r.UpdatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default Actions;
